import { useLocalStorage } from '@vueuse/core'

interface PrintOrderOptions {
  buuid?: string
  fallbackPath?: string
}

export const usePrintWrapper = () => {
  const log = useLogger('composable:usePrintWrapper')
  const { printToServer } = useLocalPrintServer()
  const localPrintServerEnabled = useLocalStorage('localPrintServerEnabled', false)

  /**
   * Main print function with fallback chain
   * @param orderData - Full order object
   * @param options - Additional options
   */
  const printOrder = async (orderData: any, options: PrintOrderOptions = {}): Promise<void> => {
    try {
      const { fallbackPath } = options

      // Case 1: Basic browser print (no data provided)
      if (!orderData) {
        window.print()
        return
      }

      // Extract order ID for navigation purposes
      const orderId = orderData.order?.id

      // Determine the navigation path
      const orderPath = fallbackPath || `/store/pos/orders/${orderId}`

      // STEP 1: Try local print server (if enabled)
      if (localPrintServerEnabled.value) {
        try {
          console.log('🖨️ Attempting local print server...')
          await printToServer(orderData)
          console.log('✅ Local print server successful')
          return // Success! Exit early
        }
        catch (error) {
          console.log('❌ Local print server failed:', error)
          useErrorToast('Local print server failed, trying alternative methods...')
          // Continue to next method (don't return)
        }
      }

      // STEP 2: Try tappyPrint (silent failure)
      if (window?.tappyPrint) {
        try {
          console.log('🖨️ Attempting tappyPrint...')
          await window.tappyPrint(orderPath)
          console.log('✅ TappyPrint successful')
          return // Success! Exit early
        }
        catch (error) {
          console.log('❌ TappyPrint failed silently:', error)
          // Continue to next method (silent failure)
        }
      }

      // STEP 3: Fallback to browser native print
      console.log('🖨️ Using browser native print...')
      window.print()
      console.log('✅ Browser print executed')
    }
    catch (error) {
      log.error('Failed to handle print operation:', error)
      useErrorToast('Failed to print order')
      throw error
    }
  }

  return {
    printOrder,
  }
}

// Global type declaration for tappyPrint
declare global {
  interface Window {
    tappyPrint?: (path: string) => Promise<void>
  }
}
